import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth, type LoginFailureReason } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import { Loader2, LogIn, ShieldCheck, Settings, Truck } from "lucide-react";

const ROLE_LABELS: Record<string, string> = {
  admin: "管理員",
  staff: "員工",
  driver: "司機",
};

const LOGIN_ERROR_MESSAGES: Record<LoginFailureReason, string> = {
  invalid_credentials: "帳號或密碼錯誤",
  rate_limited: "登入暫時受限，請稍後再試",
  service_unavailable: "無法連線或服務異常，請稍後再試",
  session_unavailable: "登入驗證已送出，但工作階段尚未建立，請重新整理後再試",
};

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();
  const { isAuthenticated, user, login, logout } = useAuth();
  const [location, setLocation] = useLocation();

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!username || !password) {
      toast({
        title: "請填寫完整",
        description: "帳號與密碼不能為空",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      toast({ title: "登入中", description: "正在驗證帳號與密碼..." });

      const result = await login(username, password);

      if (result.ok) {
        setUsername("");
        setPassword("");
        toast({
          title: "登入成功",
          description: "歡迎回來",
          duration: 2000,
        });
      } else {
        toast({
          title: "登入失敗",
          description: LOGIN_ERROR_MESSAGES[result.reason],
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "登入失敗",
        description: LOGIN_ERROR_MESSAGES.service_unavailable,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      toast({ title: "登出中", description: "正在清除會話..." });
      const success = await logout();
      if (success) {
        toast({
          title: "登出成功",
          description: "已安全登出",
          duration: 2000,
        });
        // Reload to ensure clean state
        setTimeout(() => window.location.reload(), 300);
      } else {
        toast({
          title: "登出可能未完整完成",
          description: "系統將重新整理頁面以清除狀態",
          duration: 5000,
        });
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch {
      toast({
          title: "登出時發生錯誤",
          description: "系統將重新載入頁面",
        variant: "destructive",
        duration: 3000,
      });
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  return (
    <>
      {/* Authenticated: show user info + logout (used in top nav) */}
      {isAuthenticated && user ? (
        <div className="flex items-center gap-4" id="authUserInfo">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-progress-tint text-progress flex items-center justify-center font-bold">
              {user.displayName.charAt(0)}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-ink leading-tight">
                {user.displayName}
              </span>
              <span className="text-xs text-ink-muted">
                {ROLE_LABELS[user.role] || user.role}
              </span>
            </div>
          </div>
          <div className="h-6 w-px bg-line-strong mx-1"></div>
          {/* IMP-1.7：/manage 開放 admin/staff（見 App.tsx RequireAuth），故 staff 也應見入口，
              否則被授權的歷史/月結/採購對日常主力操作者形同隱藏。 */}
          {(user.role === "admin" || user.role === "staff") && !location.startsWith("/manage") && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setLocation("/manage")}
              id="navManageBtn"
              className="bg-raised hover:bg-raised text-ink border border-line-strong shadow-sm"
            >
              <Settings className="w-4 h-4 mr-2" />
              管理中心
            </Button>
          )}
          {user.role === "driver" && location !== "/driver" && !location.startsWith("/sign/") && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setLocation("/driver")}
              id="navDriverBtn"
              className="bg-raised hover:bg-raised text-ink border border-line-strong shadow-sm"
            >
              <Truck className="w-4 h-4 mr-2" />
              配送簽收
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            id="navLogoutBtn"
            className="text-ink-muted hover:text-block hover:bg-block-tint transition-colors"
          >
            登出
          </Button>
        </div>
      ) : (
        /* Not authenticated: Standalone Glassmorphic Login Card */
        <div className="w-full max-w-md mx-auto glass-card rounded-2xl p-8">
          <div>
            <div className="flex flex-col items-center justify-center mb-8">
              <div className="w-16 h-16 bg-progress-tint rounded-2xl flex items-center justify-center mb-4 border border-progress/25">
                <ShieldCheck className="w-8 h-8 text-progress" />
              </div>
              <h2 className="text-2xl font-bold text-ink-strong">
                系統登入
              </h2>
              <p className="text-sm text-ink-muted mt-2">請輸入帳號與密碼以繼續</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="loginUsername" className="text-sm font-medium text-ink">
                  帳號
                </Label>
                <Input
                  type="text"
                  id="loginUsername"
                  placeholder="admin"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="glass-input h-11"
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="loginPassword" className="text-sm font-medium text-ink">
                    密碼
                  </Label>
                </div>
                <Input
                  type="password"
                  id="loginPassword"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="glass-input h-11"
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                id="loginSubmitBtn"
                className="w-full h-11 mt-6 bg-action hover:bg-action-hover text-white shadow-sm border-none transition-all duration-300"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    驗證中...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    登入系統
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
